/**
 * NotificationDetailModal - 알림 상세 정보 모달
 * 알림 발송 정보를 상세하게 표시
 */

import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  notificationLogsRepository,
  NotificationLog,
} from '../repositories/NotificationLogsRepository';

interface NotificationDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  log: NotificationLog | null;
}

const NotificationDetailModal: React.FC<NotificationDetailModalProps> = ({
  isOpen,
  onClose,
  log,
}) => {
  if (!isOpen || !log) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* 배경 오버레이 */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* 모달 컨텐츠 */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
          {/* 헤더 */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">알림 상세 정보</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* 본문 */}
          <div className="p-6 space-y-6">
            {/* 발송 상태 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                발송 상태
              </label>
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${notificationLogsRepository.getStatusStyle(
                  log.status
                )}`}
              >
                {notificationLogsRepository.getStatusLabel(log.status)}
              </span>
            </div>

            {/* 알림 타입 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                알림 타입
              </label>
              <p className="text-gray-900">
                {notificationLogsRepository.getNotificationTypeLabel(log.notification_type)}
              </p>
            </div>

            {/* 수신자 정보 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  수신자 이름
                </label>
                <p className="text-gray-900">{log.user_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  전화번호
                </label>
                <p className="text-gray-900">{log.phone}</p>
              </div>
            </div>

            {/* 메시지 내용 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                메시지 내용
              </label>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-gray-900 whitespace-pre-wrap">{log.message}</p>
              </div>
            </div>

            {/* 발송 시간 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                발송 시간
              </label>
              <p className="text-gray-900">
                {notificationLogsRepository.formatDateTime(log.sent_at)}
              </p>
            </div>

            {/* 추가 정보 (metadata) */}
            {log.metadata && Object.keys(log.metadata).length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  추가 정보
                </label>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <dl className="space-y-2">
                    {log.metadata.gate_id && (
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-600">게이트 ID:</dt>
                        <dd className="text-sm font-medium text-gray-900">
                          {log.metadata.gate_id}
                        </dd>
                      </div>
                    )}
                    {log.metadata.action && (
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-600">입출입 타입:</dt>
                        <dd className="text-sm font-medium text-gray-900">
                          {log.metadata.action === 'entry' ? '입장' :
                           log.metadata.action === 'outing' ? '외출' : '퇴장'}
                        </dd>
                      </div>
                    )}
                    {log.metadata.seat_number && (
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-600">좌석 번호:</dt>
                        <dd className="text-sm font-medium text-gray-900">
                          {log.metadata.seat_number}번
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>
            )}

            {/* 템플릿 코드 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                템플릿 코드
              </label>
              <p className="text-sm text-gray-600 font-mono">{log.template_code}</p>
            </div>
          </div>

          {/* 푸터 */}
          <div className="flex justify-end p-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationDetailModal;
